import { Injectable } from '@angular/core';
import { Affected } from '@ess-front/shared/models';
import { Experience } from '@app/utils/models/itinerary/experience.model';
import { ItineraryState } from '@app/utils/models/store/itinerary-state.model';

@Injectable({
  providedIn: 'root',
})
export class ItineraryReducerService {
  updateExperience(state: ItineraryState, experienceId: number, updatedExperience: Experience): ItineraryState {
    const days = state.days.map(day => ({
      ...day,
      experiences: day.experiences.map(experience => (experience.id === experienceId ? updatedExperience : experience)),
    }));
    return { ...state, days };
  }

  deleteExperience(state: ItineraryState, experienceId: number): ItineraryState {
    const days = state.days.map(day => ({
      ...day,
      experiences: day.experiences.filter(experience => experience.id !== experienceId),
    }));
    return { ...state, days };
  }

  syncAffectedExperiences(state: ItineraryState, { created, deleted, updated }: Affected<Experience>): ItineraryState {
    const deletedIds = new Set(deleted);
    const updatedExperiencesById = new Map(updated.map(experience => [experience.id, experience]));
    const createdExperiencesByDate = this.groupExperiencesByDate(created);

    const days = state.days.map(day => {
      const experiences = day.experiences
        .filter(experience => !deletedIds.has(experience.id))
        .map(experience => updatedExperiencesById.get(experience.id) ?? experience);
      const createdExperiences = day.date !== null ? (createdExperiencesByDate.get(day.date) ?? []) : [];
      if (createdExperiences.length === 0) {
        return { ...day, experiences };
      }
      return { ...day, experiences: this.sortExperiencesByStartDatetime([...experiences, ...createdExperiences]) };
    });
    return { ...state, days };
  }

  private groupExperiencesByDate(experiences: Experience[]): Map<string, Experience[]> {
    const experiencesByDate = new Map<string, Experience[]>();
    for (const experience of experiences) {
      const date = this.getExperienceDate(experience);
      if (date === null) {
        continue;
      }
      const group = experiencesByDate.get(date);
      if (group) {
        group.push(experience);
      } else {
        experiencesByDate.set(date, [experience]);
      }
    }
    return experiencesByDate;
  }

  private getExperienceDate(experience: Experience): string | null {
    return experience.startDate ?? experience.startDatetime?.split('T')[0] ?? null;
  }

  private sortExperiencesByStartDatetime(experiences: Experience[]): Experience[] {
    return [...experiences].sort((experienceA, experienceB) => {
      if (!experienceA.startDatetime) return experienceB.startDatetime ? 1 : 0;
      if (!experienceB.startDatetime) return -1;
      return new Date(experienceA.startDatetime).getTime() - new Date(experienceB.startDatetime).getTime();
    });
  }
}
